import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import OrdenWebDetallePendiente from './modals/OrdenWebDetallePendiente';
import OrdenWebDetalleEnProceso from './modals/OrdenWebDetalleEnProceso';
import OrdenWebDetalleEnRuta from './modals/OrdenWebDetalleEnRuta';

/* ===== Helpers mínimos ===== */
const resolveApiBase = () => {
  const env = process.env.REACT_APP_API_BASE;
  if (env && env.trim()) return env.replace(/\/+$/, '');
  const isLocal = ['localhost','127.0.0.1'].includes(window.location.hostname);
  // En local apuntamos al backend local; en cloud devolvemos '' para usar /api relativo
  return isLocal ? 'http://localhost:5001' : '';
};
const API_BASE = resolveApiBase();

const fileUrl = (p) => {
  if (!p) return `${API_BASE}/public/nodisponible.png`;
  const s = String(p).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith('data:')) return s;
  const norm = s.startsWith('/') ? s : `/${s}`;
  return `${API_BASE}${norm}`;
};

const fetchJSON = async (url, opts = {}) => {
  const resp = await fetch(url, { credentials: 'include', ...opts });
  let j = null;
  try { j = await resp.json(); } catch {}
  if (!resp.ok || (j && j.ok === false)) {
    const msg = (j && j.msg) || resp.statusText || `HTTP ${resp.status}`;
    throw new Error(msg);
  }
  return j ?? {};
};
/* =========================== */

const TABS = [
  { key: 'pendientes', label: 'Pendiente' },
  { key: 'enProceso',  label: 'En Proceso' },
  { key: 'enRuta',     label: 'En Ruta' },
];

export default function OrdenesWeb() {
  const [active, setActive]   = useState('pendientes');
  const [data, setData]       = useState({ pendientes: [], enProceso: [], enRuta: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');
  const [lastUpdated, setLastUpdated] = useState(null);

  const [modal, setModal] = useState({ open:false, estado:null, id:null, detalle:null });
  const didInit = useRef(false);

  const fetchDashboard = useCallback(async (signal) => {
    setLoading(true);
    setErr('');
    try {
      const j = await fetchJSON(`${API_BASE}/api/web/pedidos/dashboard?limit=50`, { signal });

      // ⬇️ Filtrar 'Entregado' del bucket enRuta
      const enRutaFiltradas = (j.enRuta || []).filter(
        x => String(x.estado || '').toLowerCase() !== 'entregado'
      );

      setData({
        pendientes: j.pendientes || [],
        enProceso:  j.enProceso  || [],
        enRuta:     enRutaFiltradas,
      });
      setLastUpdated(new Date());
    } catch (e) {
      if (e.name !== 'AbortError') setErr(e.message || 'Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  // Cargar al entrar + auto-refresh 5 min
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;

    const ac = new AbortController();
    fetchDashboard(ac.signal); // carga inmediata

    const id = setInterval(() => fetchDashboard(), 5 * 60 * 1000);
    return () => { clearInterval(id); ac.abort(); };
  }, [fetchDashboard]);

  const activeList = useMemo(() => data[active] || [], [data, active]);

  const openModal = async (pedido) => {
    try {
      const j = await fetchJSON(`${API_BASE}/api/web/pedidos/id/${pedido.id}`);
      setModal({ open: true, estado: pedido.estado, id: pedido.id, detalle: j.pedido });
    } catch (e) {
      setErr(e.message || 'Error cargando detalle');
    }
  };
  const closeModal = () => setModal({ open:false, estado:null, id:null, detalle:null });

  // actualizar UI local al cambiar estado
  const removeEverywhere = (id) => setData(prev => ({
    pendientes: prev.pendientes.filter(x => x.id !== id),
    enProceso:  prev.enProceso.filter(x => x.id !== id),
    enRuta:     prev.enRuta.filter(x => x.id !== id),
  }));
  const addToBucket = (estado, pedido) => setData(prev => {
    const p = { ...prev };
    if (estado === 'Pendiente') p.pendientes = [pedido, ...p.pendientes];
    if (estado === 'enProceso') p.enProceso  = [pedido, ...p.enProceso];
    if (estado === 'enRuta')    p.enRuta     = [pedido, ...p.enRuta];
    return p;
  });

  const onEstadoCambiado = (id, nuevoEstado, overrideFields = {}) => {
    const d = modal.detalle;
    const base = d ? {
      id: d.id,
      usuarioId: d.usuarioId,
      tipoPedido: d.tipoPedido,
      cliente: d.cliente,
      dpiCliente: d.dpiCliente,
      whatsapp: d.whatsapp,
      codigo: d.codigo,
      boleta: d.boleta,
      banco: d.banco,
      total: d.total,
      estado: nuevoEstado,
      created_at: d.created_at,
      imagen: d.imagen,
      pedido: d.pedido,
      direccionEntrega: d.direccionEntrega,
      ...overrideFields,
    } : null;

    removeEverywhere(id);
    if (base && ['Pendiente','enProceso','enRuta'].includes(nuevoEstado)) {
      addToBucket(nuevoEstado, base);
    }
    // Si lo marcan Entregado desde En Ruta, ya no vuelve a aparecer
    closeModal();
  };

  return (
    <div className="p-4 sm:p-6">
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Órdenes desde la Web</h1>
          <p className="text-sm text-gray-600">
            Resumen por estado
            {lastUpdated && <> • Actualizado: {lastUpdated.toLocaleTimeString()}</>}
          </p>
        </div>
        <button
          onClick={() => fetchDashboard()}
          className="px-4 py-2 rounded-xl bg-sky-600 text-white hover:bg-sky-700"
        >
          Actualizar
        </button>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <KPI title="Pendiente" value={data.pendientes.length} />
        <KPI title="En Proceso" value={data.enProceso.length} />
        <KPI title="En Ruta" value={data.enRuta.length} />
      </section>

      {/* Tabs */}
      <nav className="mt-6 flex gap-2 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setActive(t.key)}
            className={`px-4 py-2 rounded-xl border transition whitespace-nowrap ${
              active === t.key
                ? 'bg-sky-600 text-white border-sky-600'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {t.label}
            <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-black/10">
              {data[t.key]?.length || 0}
            </span>
          </button>
        ))}
      </nav>

      {/* Tabla */}
      <section className="mt-4">
        {err && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-red-700">
            {err}
          </div>
        )}
        {loading ? <SkeletonTable /> : <TableOrdenes rows={activeList} onVer={openModal} />}
      </section>

      {/* Modales */}
      {modal.open && modal.estado === 'Pendiente' && (
        <OrdenWebDetallePendiente
          API_BASE={API_BASE}
          fileUrl={fileUrl}
          pedido={modal.detalle}
          onClose={closeModal}
          onEstadoCambiado={onEstadoCambiado}
        />
      )}
      {modal.open && modal.estado === 'enProceso' && (
        <OrdenWebDetalleEnProceso
          API_BASE={API_BASE}
          fileUrl={fileUrl}
          pedido={modal.detalle}
          onClose={closeModal}
          onEstadoCambiado={onEstadoCambiado}
        />
      )}
      {modal.open && modal.estado === 'enRuta' && (
        <OrdenWebDetalleEnRuta
          API_BASE={API_BASE}
          fileUrl={fileUrl}
          pedido={modal.detalle}
          onClose={closeModal}
          onEstadoCambiado={onEstadoCambiado}
        />
      )}
    </div>
  );
}

/* --------- UI helpers ---------- */
function KPI({ title, value }) {
  return (
    <div className="rounded-2xl border border-gray-200 p-4 bg-white shadow-sm">
      <div className="text-sm text-gray-500">{title}</div>
      <div className="text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}

function SkeletonTable() {
  const headers = ['Cliente','Nombre','DPI','Boleta','Banco','WhatsApp','Total','Estado','Fecha','Acciones'];
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>{headers.map(h => <th key={h} className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{h}</th>)}</tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }).map((_, i) => (
            <tr key={i} className="border-t">
              {Array.from({ length: headers.length }).map((__, j) => (
                <td key={j} className="px-4 py-3">
                  <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableOrdenes({ rows = [], onVer }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-x-auto">
      <table className="min-w-full">
        <thead className="bg-gray-50">
          <tr>
            <Th>Cliente</Th>
            <Th>Nombre</Th>
            <Th>DPI</Th>
            <Th>Boleta</Th>
            <Th>Banco</Th>
            <Th>WhatsApp</Th>
            <Th>Total</Th>
            <Th>Estado</Th>
            <Th>Fecha</Th>
            <Th>Acciones</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={10} className="px-4 py-6 text-center text-gray-500">Sin registros en este estado.</td></tr>
          ) : rows.map(r => (
            <tr key={r.id} className="border-t hover:bg-gray-50">
              <Td>{r.usuarioId ? `Cliente #${r.usuarioId}` : 'No es cliente'}</Td>
              <Td>{r.cliente || '—'}</Td>
              <Td>{r.dpiCliente || '—'}</Td>
              <Td>{r.boleta || '—'}</Td>
              <Td>{r.banco || '—'}</Td>
              <Td>{r.whatsapp || '—'}</Td>
              <Td>Q{Number(r.total || 0).toFixed(2)}</Td>
              <Td><EstadoBadge value={r.estado} /></Td>
              <Td>{safeDate(r.created_at)}</Td>
              <Td>
                <button
                  className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-100"
                  onClick={() => onVer(r)}
                >
                  Ver
                </button>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children }) { return <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">{children}</th>; }
function Td({ children }) { return <td className="px-4 py-3">{children}</td>; }

function EstadoBadge({ value }) {
  const m = {
    Pendiente:'bg-amber-100 text-amber-800 border-amber-200',
    enProceso:'bg-sky-100 text-sky-800 border-sky-200',
    enRuta:'bg-emerald-100 text-emerald-800 border-emerald-200',
    Entregado:'bg-emerald-200 text-emerald-900 border-emerald-300',
    Cancelado:'bg-rose-100 text-rose-800 border-rose-200',
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full border ${m[value] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{value}</span>;
}

function safeDate(d){ try { return new Date(d).toLocaleString(); } catch { return '—'; } }
